import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Zap, Save, Loader2, Percent } from 'lucide-react';
import { useOfferStore } from '@/lib/store';
import { useToast } from '@/hooks/use-toast';
import { CatalogSuggestDialog } from './catalog-suggest-dialog';
import { CatalogBatchSuggestDialog } from './catalog-batch-suggest-dialog';
import {
  BLITZ_ZEILEN_IDS,
  baueBlitzKalkulation,
  berechneKalkulation,
  berechneMengenBezugWechselVorschau,
  brauchtErsteEingabeBezugsHinweis,
  darfKalkulationPersistiertWerden,
  einheitspreisAusKalkulation,
  positionsGesamtbeitrag,
  roundMoney,
  summeAllerPositionen,
  warntMischungEinheitMitPositionszeile,
  wechsleMengenBezug,
  wendeGesamtaufschlagAn,
  type GesamtaufschlagPosition,
} from '@shared/kalkulation';
import type { Position, PositionsKalkulation } from '@shared/schema';

function parseInput(value: string): number | undefined {
  if (value.trim() === '') return undefined;
  const n = Number(value.replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

/** Wie parseInput, erlaubt aber auch negative Werte (für ±Prozent beim Gesamtaufschlag). */
function parseSignedInput(value: string): number | undefined {
  if (value.trim() === '') return undefined;
  const n = Number(value.replace(',', '.'));
  return Number.isFinite(n) ? n : undefined;
}

function formatEuro(value: number): string {
  return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

type BlitzSpalte = 'stunden' | 'materialEk' | 'sonstiges';

/**
 * P2-4 (Fix): das Blitz-Eingabefeld war ein unkontrolliertes `<input>` (`defaultValue`) —
 * `defaultValue` wird von React nur beim ERSTEN Mount in den DOM-Wert übernommen. Ersetzt ein
 * Katalog-/X84-Preis-Import EXTERN (außerhalb dieses Inputs, z.B. über CatalogSuggestDialog)
 * den Material-EK einer bestehenden 'einheit'-Kalkulation, ändert sich `position.kalkulation`
 * im Store, aber das längst gemountete `<input>`-Element zeigt weiter den ALTEN Wert — Summen
 * und der gespeicherte Preis nutzen bereits den neuen EK, das Feld zeigt den alten (angezeigt ≠
 * gespeichert).
 *
 * Fix: ein Ref auf den zuletzt SELBST committeten Wert (über onChange dieses Inputs) merken.
 * Weicht der von außen hereinkommende `value`-Prop davon ab, kam die Änderung von AUSSEN
 * (Katalog-Import) — dann wird der DOM-Wert imperativ nachgezogen. Tippt der Nutzer selbst,
 * committet onChange sofort in den Ref, sodass der nachfolgende Effect-Lauf keinen Unterschied
 * sieht und NICHT eingreift (kein Remount/Cursor-Sprung bei jedem Tastendruck — das wäre eine
 * neue Regression gewesen).
 */
function BlitzZellInput({
  positionId,
  spalte,
  value,
  placeholder,
  onCommit,
  onEnter,
}: {
  positionId: number;
  spalte: BlitzSpalte;
  value: number | undefined;
  placeholder: string;
  onCommit: (value: number | undefined) => void;
  onEnter: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const lastCommittedRef = useRef<number | undefined>(value);

  useEffect(() => {
    if (value !== lastCommittedRef.current && inputRef.current) {
      inputRef.current.value = value !== undefined ? String(value).replace('.', ',') : '';
      lastCommittedRef.current = value;
    }
  }, [value]);

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      data-blitz-spalte={spalte}
      data-blitz-position={positionId}
      defaultValue={value !== undefined ? String(value).replace('.', ',') : ''}
      onChange={(e) => {
        const parsed = parseInput(e.target.value);
        if (e.target.value.trim() === '' || parsed !== undefined) {
          lastCommittedRef.current = parsed;
          onCommit(parsed);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onEnter();
        }
      }}
      placeholder={placeholder}
      className="h-7 w-full rounded border border-gray-200 px-2 text-right text-sm focus:border-blue-400 focus:outline-none"
    />
  );
}

/**
 * Blitzkalkulation: Stundensatz + Material-Aufschlag einmal oben einstellen,
 * dann pro Position nur noch Stunden / Material-EK / Sonstiges eintragen.
 * Enter springt zur nächsten Zeile derselben Spalte.
 */
export function BlitzkalkulationView() {
  const positions = useOfferStore((s) => s.positions);
  const quoteItems = useOfferStore((s) => s.quoteItems);
  const defaults = useOfferStore((s) => s.kalkulationsDefaults);
  const setKalkulationsDefaults = useOfferStore((s) => s.setKalkulationsDefaults);
  const setKalkulationsView = useOfferStore((s) => s.setKalkulationsView);
  const updatePositionKalkulation = useOfferStore((s) => s.updatePositionKalkulation);
  const updatePosition = useOfferStore((s) => s.updatePosition);
  const { toast } = useToast();

  const [isSavingDefaults, setIsSavingDefaults] = useState(false);
  const [defaultsLoaded, setDefaultsLoaded] = useState(false);
  const [gesamtaufschlagProzent, setGesamtaufschlagProzent] = useState('');
  const [gesamtaufschlagBezeichnung, setGesamtaufschlagBezeichnung] = useState('');
  // P1-1 (Fix, Codex-Fund): erzwingt einen Remount der betroffenen BlitzZellInput-Zelle, wenn
  // updatePositionKalkulation() das Update ablehnt (Netto <= 0 nach dem Update) — siehe
  // applyBlitz weiter unten. Ein Remount lässt die Zelle ihren `defaultValue` erneut aus dem
  // (unveränderten, weil abgelehnten) Store-Wert ziehen, statt den bereits im DOM stehenden,
  // NIE gespeicherten getippten Wert stehen zu lassen.
  const [blitzRejectNonce, setBlitzRejectNonce] = useState<Record<string, number>>({});
  // Review-Auflage C (GPT-5.6/Grok, 2026-08): der frühere lokale Vormerk `pendingMengenBezug`
  // (Umschalter-Wahl VOR der ersten gespeicherten Kalkulation) wurde ersatzlos entfernt. Ein
  // Bezug, der nur im UI-State lebt, läuft auseinander: Reload verwirft ihn (die nächste
  // Eingabe speichert dann still den Default), und andere Schreibwege (Katalog-/X84-Import,
  // Kopie, Vorlage, zweiter Tab) können die erste Kalkulation anlegen, ohne ihn zu kennen.
  // Stattdessen gilt: der Bezug existiert AUSSCHLIESSLICH als persistierter Modellzustand
  // (kalkulation.mengenBezug). Vor der ersten Eingabe ist der Umschalter deaktiviert (es gibt
  // nichts umzustellen); die erste Eingabe stempelt IMMER explizit 'einheit' (setzeBlitzZeile)
  // und macht das bei Menge ≠ 1 per Toast unübersehbar (Auflage D, siehe applyBlitz); danach
  // ist der Umschalter aktiv und bestätigungspflichtig (Auflage A, siehe setzeMengenBezug).
  const containerRef = useRef<HTMLDivElement>(null);

  // Globale Defaults aus den Einstellungen laden (einmalig)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/pdf-settings', { credentials: 'include' });
        if (!res.ok) return;
        const json = await res.json();
        const data = json?.data;
        if (!cancelled && data) {
          setKalkulationsDefaults({
            stundensatz: typeof data.defaultHourlyRate === 'number' ? data.defaultHourlyRate : undefined,
            materialAufschlagProzent:
              typeof data.defaultMaterialMarkupPercent === 'number' ? data.defaultMaterialMarkupPercent : undefined,
          });
        }
      } catch {
        // Defaults bleiben auf Fallback
      } finally {
        if (!cancelled) setDefaultsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setKalkulationsDefaults]);

  const saveDefaults = async () => {
    setIsSavingDefaults(true);
    try {
      const res = await fetch('/api/pdf-settings', { credentials: 'include' });
      const json = res.ok ? await res.json() : { data: {} };
      const merged = {
        ...(json?.data || {}),
        defaultHourlyRate: defaults.stundensatz,
        defaultMaterialMarkupPercent: defaults.materialAufschlagProzent,
      };
      await fetch('/api/pdf-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(merged),
      });
    } catch {
      // Fehler beim Speichern der Defaults ist nicht kritisch für die Kalkulation
    } finally {
      setIsSavingDefaults(false);
    }
  };

  const mengeById = useMemo(() => {
    const map = new Map<number, number>();
    for (const item of quoteItems) {
      map.set(item.id, Number(item.menge) || 0);
    }
    return map;
  }, [quoteItems]);

  const rows = positions;

  // Gesamtaufschlag/Rabatt: einmaliger, multiplikativer Auf-/Abschlag auf den EP aller
  // Positionen. Bei Positionen MIT Tiefenkalkulation wird eine nachvollziehbare
  // Sonstiges-Zeile in die Kalkulation eingefügt (siehe shared/kalkulation.ts).
  const gesamtaufschlagBasis: GesamtaufschlagPosition[] = useMemo(
    () =>
      rows.map((position) => {
        const menge = mengeById.get(position.id) ?? 0;
        return {
          id: position.id,
          einheitspreis: Number(position.preis) || 0,
          menge: menge > 0 ? menge : 1,
          kalkulation: position.kalkulation,
        };
      }),
    [rows, mengeById],
  );

  const gesamtaufschlagProzentZahl = parseSignedInput(gesamtaufschlagProzent);

  const gesamtaufschlagVorherSumme = useMemo(
    () => summeAllerPositionen(gesamtaufschlagBasis),
    [gesamtaufschlagBasis],
  );

  const gesamtaufschlagErgebnisse = useMemo(() => {
    if (gesamtaufschlagProzentZahl === undefined || gesamtaufschlagProzentZahl === 0) return null;
    if (gesamtaufschlagBasis.length === 0) return null;
    const bezeichnung =
      gesamtaufschlagBezeichnung.trim() || (gesamtaufschlagProzentZahl >= 0 ? 'Wagnis & Gewinn' : 'Rabatt');
    return wendeGesamtaufschlagAn(gesamtaufschlagBasis, gesamtaufschlagProzentZahl, bezeichnung, defaults);
  }, [gesamtaufschlagBasis, gesamtaufschlagProzentZahl, gesamtaufschlagBezeichnung, defaults]);

  const gesamtaufschlagNachherSumme = useMemo(() => {
    if (!gesamtaufschlagErgebnisse) return null;
    return roundMoney(
      gesamtaufschlagErgebnisse.reduce(
        (sum, ergebnis, index) => sum + ergebnis.neuerEinheitspreis * gesamtaufschlagBasis[index].menge,
        0,
      ),
    );
  }, [gesamtaufschlagErgebnisse, gesamtaufschlagBasis]);

  const anwendenGesamtaufschlag = () => {
    if (!gesamtaufschlagErgebnisse) return;

    // P1 (Fix, Codex-Fund): updatePositionKalkulation() lehnt eine befüllte, aber für die
    // EP-Ableitung ungültige Kalkulation (Netto <= 0 — z.B. genau DIESER globale Rabatt, der
    // eine bestehende Kalkulation exakt/über die Nullgrenze hinaus aufhebt) STILL ab (siehe
    // shared/kalkulation.ts::darfKalkulationPersistiertWerden). Ohne Vorab-Prüfung würde die
    // Schleife unten für einzelne Positionen erfolgreich schreiben, für andere lautlos
    // scheitern — ein Teil-Commit, den das Formular anschließend durch Leeren als
    // vollständigen Erfolg ausgibt. Fix: ALLE betroffenen Positionen VORAB gegen dasselbe
    // Gate prüfen (all-or-nothing) — bei auch nur einer Ablehnung wird GAR NICHTS
    // angewendet und der Nutzer bekommt eine konkrete, actionable Meldung.
    const abgelehnt = gesamtaufschlagErgebnisse.filter(
      (ergebnis) =>
        ergebnis.neueKalkulation && !darfKalkulationPersistiertWerden(ergebnis.neueKalkulation, defaults),
    );
    if (abgelehnt.length > 0) {
      toast({
        title: 'Gesamtaufschlag nicht angewendet',
        description:
          `${abgelehnt.length} von ${gesamtaufschlagErgebnisse.length} Position(en) würden dadurch einen ` +
          'nicht-positiven Betrag ergeben (0,00 € oder negativ) — nichts wurde übernommen. ' +
          'Bitte den Prozentsatz anpassen.',
        variant: 'destructive',
      });
      return;
    }

    const bestaetigt = window.confirm(
      `Netto ${formatEuro(gesamtaufschlagVorherSumme)} → ${formatEuro(
        gesamtaufschlagNachherSumme ?? gesamtaufschlagVorherSumme,
      )}. Anwenden?`,
    );
    if (!bestaetigt) return;

    // Trotz der Vorab-Prüfung defensiv das tatsächliche Erfolgssignal auswerten (z.B. falls
    // eine Position zwischen Prüfung und Anwendung entfernt wurde) — NIE stillschweigend
    // Erfolg annehmen, NIE das Formular leeren, wenn tatsächlich etwas fehlgeschlagen ist.
    let fehlgeschlagen = 0;
    for (const ergebnis of gesamtaufschlagErgebnisse) {
      if (ergebnis.neueKalkulation) {
        const erfolgreich = updatePositionKalkulation(ergebnis.id, ergebnis.neueKalkulation);
        if (!erfolgreich) fehlgeschlagen += 1;
      } else {
        updatePosition(ergebnis.id, { preis: ergebnis.neuerEinheitspreis });
      }
    }
    if (fehlgeschlagen > 0) {
      toast({
        title: 'Gesamtaufschlag teilweise angewendet',
        description: `${fehlgeschlagen} Position(en) konnten nicht aktualisiert werden (evtl. zwischenzeitlich entfernt). Bitte prüfen.`,
        variant: 'destructive',
      });
      return; // Formular NICHT leeren — der Nutzer soll den inkonsistenten Rest noch sehen können.
    }
    setGesamtaufschlagProzent('');
    setGesamtaufschlagBezeichnung('');
  };

  const applyBlitz = (positionId: number, spalte: BlitzSpalte, wert: number | undefined) => {
    const state = useOfferStore.getState();
    const position = state.positions.find((p) => p.id === positionId);
    if (!position) return;

    // P1-1: NUR ein tatsächlich eingetragener Wert (nicht das Leeren eines Felds) kann die
    // gefährliche Mischung auslösen — auf dem Stand VOR setzeBlitzZeile prüfen, damit die
    // Materialzeile (falls vorhanden) noch die ist, die den 'einheit'-Stempel begründet.
    if (wert !== undefined && (spalte === 'stunden' || spalte === 'sonstiges')) {
      const art = spalte === 'stunden' ? 'lohn' : 'sonstiges';
      if (warntMischungEinheitMitPositionszeile(position.kalkulation ?? null, art)) {
        toast({
          title: 'Achtung: Kalkulation gilt „je Einheit"',
          description:
            'Diese Position wurde aus einem Katalog-/Preis-Import übernommen und rechnet PRO EINHEIT. ' +
            'Der neue Wert wird beim Gesamtbetrag mit der Positionsmenge multipliziert — bitte prüfen, ' +
            'ob der eingetragene Betrag wirklich „je Einheit" statt „für die gesamte Position" gilt.',
          variant: 'destructive',
        });
      }
    }

    const art: keyof typeof BLITZ_ZEILEN_IDS =
      spalte === 'stunden' ? 'lohn' : spalte === 'materialEk' ? 'material' : 'sonstiges';
    const neueKalkulation = baueBlitzKalkulation(
      position.kalkulation,
      art,
      spalte === 'stunden' ? { menge: wert } : { einzelpreis: wert },
    );

    // Review-Auflage D (Grok, 2026-08): entsteht hier die ERSTE Kalkulation dieser Position
    // (Default-Stempel 'einheit' durch setzeBlitzZeile) und ist die Positionsmenge ≠ 1, muss
    // der Nutzer den Bezug BEWUSST sehen — die Bildunterschrift allein reicht nicht. VOR dem
    // Speichern auf dem alten Stand ermitteln (danach existiert die Kalkulation bereits).
    // Bewusst ein unübersehbarer Toast statt eines blockierenden Dialogs: applyBlitz feuert
    // bei JEDEM Tastendruck (onChange) — ein window.confirm würde den Nutzer mitten im Tippen
    // von "0,35" nach der ersten Ziffer unterbrechen.
    const menge = Number(state.quoteItems.find((q) => q.id === positionId)?.menge) || 0;
    const zeigeErsteEingabeHinweis =
      neueKalkulation !== null && brauchtErsteEingabeBezugsHinweis(position.kalkulation, menge);

    // P1-1 (Fix, Codex-Fund): updatePositionKalkulation() gibt jetzt boolean zurück; bei einer
    // befüllten, aber für die EP-Ableitung ungültigen Kalkulation (Netto <= 0 — z.B. reduziert/
    // leert der Nutzer hier eine positive Zeile in einer Kalkulation, die daneben eine FIXE
    // negative Rabattzeile trägt, siehe shared/kalkulation.ts::baueBlitzKalkulation) schreibt der
    // Store GAR NICHTS. Ohne diese Prüfung hatte BlitzZellInput den getippten DOM-Wert schon
    // optimistisch angezeigt (unkontrolliertes Input) — ein ungespeicherter Phantom-Wert ohne
    // jede Warnung, der erst beim nächsten zufälligen Re-Render zurücksprang. Fix: Rückgabewert
    // auswerten, sichtbar warnen UND die Zelle aktiv auf den zuletzt tatsächlich gespeicherten
    // Wert zurückzwingen (Remount über blitzRejectNonce, siehe renderBlitzInput).
    const erfolgreich = state.updatePositionKalkulation(positionId, neueKalkulation);
    if (erfolgreich && zeigeErsteEingabeHinweis) {
      const einheitLabel = position.einheit?.trim() || 'Einheit';
      toast({
        title: `Neue Kalkulation: Werte gelten je ${einheitLabel}`,
        description:
          `Beim Gesamtbetrag wird mit der Positionsmenge (${String(menge).replace('.', ',')} ${einheitLabel}) ` +
          'multipliziert. Gilt ein Wert für die GESAMTE Position, in der Modus-Spalte links auf „gesamt" umstellen.',
      });
    }
    if (!erfolgreich) {
      const zellenSchluessel = `${positionId}-${spalte}`;
      setBlitzRejectNonce((prev) => ({ ...prev, [zellenSchluessel]: (prev[zellenSchluessel] ?? 0) + 1 }));
      toast({
        title: 'Nicht gespeichert',
        description:
          'Diese Kalkulation würde einen nicht-positiven Betrag (0,00 € oder negativ) ergeben — der ' +
          'eingegebene Wert wurde NICHT übernommen. Bitte prüfen (z. B. eine fixe Rabatt-/Sonstiges-Zeile ' +
          'in dieser Kalkulation).',
        variant: 'destructive',
      });
    }
  };

  const focusNext = (positionId: number, spalte: BlitzSpalte) => {
    const container = containerRef.current;
    if (!container) return;
    const inputs = Array.from(
      container.querySelectorAll<HTMLInputElement>(`input[data-blitz-spalte="${spalte}"]`),
    );
    const index = inputs.findIndex((el) => el.dataset.blitzPosition === String(positionId));
    const next = index >= 0 ? inputs[index + 1] : undefined;
    if (next) {
      next.focus();
      next.select();
    }
  };

  // P1-1 (Fix): berechneKalkulation(...).gesamtNetto einer 'einheit'-Position ist ein
  // PRO-EINHEIT-Betrag — der Aggregat-Beitrag zur Kopfzeilen-Summe MUSS mit der Positionsmenge
  // multipliziert werden (positionsGesamtbeitrag kapselt das), sonst unterschätzt die
  // "Kalkulierte Summe" die tatsächlichen Kosten um den Mengenfaktor.
  const gesamt = useMemo(() => {
    let lohn = 0;
    let material = 0;
    let sonstiges = 0;
    let stunden = 0;
    for (const position of rows) {
      const menge = mengeById.get(position.id) ?? 0;
      const beitrag = positionsGesamtbeitrag(position.kalkulation, menge, defaults);
      lohn += beitrag.lohnSumme;
      material += beitrag.materialSumme;
      sonstiges += beitrag.sonstigesSumme;
      stunden += beitrag.lohnStunden;
    }
    return { lohn, material, sonstiges, stunden, netto: lohn + material + sonstiges };
  }, [rows, defaults, mengeById]);

  const blitzWert = (positionId: number, spalte: BlitzSpalte): number | undefined => {
    const kalkulation = positions.find((p) => p.id === positionId)?.kalkulation;
    if (!kalkulation) return undefined;
    if (spalte === 'stunden') {
      return kalkulation.zeilen.find((z) => z.id === BLITZ_ZEILEN_IDS.lohn)?.menge;
    }
    if (spalte === 'materialEk') {
      return kalkulation.zeilen.find((z) => z.id === BLITZ_ZEILEN_IDS.material)?.einzelpreis;
    }
    return kalkulation.zeilen.find((z) => z.id === BLITZ_ZEILEN_IDS.sonstiges)?.einzelpreis;
  };

  // Vorfall Malermeister: der Modus (mengenBezug) entscheidet, ob eine eingetragene Zahl "je
  // Einheit" oder "für die gesamte Position" gilt — das MUSS an der Eingabestelle sichtbar sein,
  // nicht nur im großen Tiefenkalkulations-Dialog. Für eine bestehende Kalkulation gilt IMMER
  // deren tatsächlicher, gespeicherter mengenBezug (auch ein fehlendes Feld ⇒ 'gesamt',
  // Bestandsschutz). Für eine Position OHNE Kalkulation zeigt die UI den Default 'einheit' an,
  // den setzeBlitzZeile bei der ersten Eingabe explizit stempeln wird — es gibt keinen lokalen
  // Zwischenzustand mehr (Review-Auflage C, siehe Kommentar bei blitzRejectNonce oben).
  const effektiverMengenBezug = (position: Position): 'einheit' | 'gesamt' => {
    if (position.kalkulation) {
      return position.kalkulation.mengenBezug === 'einheit' ? 'einheit' : 'gesamt';
    }
    return 'einheit';
  };

  const setzeMengenBezug = (position: Position, ziel: 'einheit' | 'gesamt') => {
    const kalkulation = position.kalkulation;
    // Ohne gespeicherte Kalkulation ist der Umschalter deaktiviert (siehe
    // renderMengenBezugToggle) — defensiv trotzdem abfangen.
    if (!kalkulation) return;
    // Bereits EXPLIZIT auf dem Zielwert ⇒ nichts zu tun. (Fehlt das Feld, wird ein Klick auf
    // "gesamt" es explizit festschreiben — preislich wirkungslos, siehe Vorschau unten.)
    if (kalkulation.mengenBezug === ziel) return;

    // Review-Auflage A (GPT-5.6/Grok, 2026-08): der Wechsel deutet JEDE Zeile neu — z.B.
    // Lohn 7 €/m + Material 10 €/m bei 43 m: 731 € Gesamt ('einheit') vs. 17 € ('gesamt').
    // Deshalb: EP und Gesamtpreis VORHER/NACHHER zeigen und bestätigen lassen. Nur ein
    // preislich wirkungsloser Wechsel (z.B. Menge = 1 oder Bestand ohne Feld → explizit
    // 'gesamt') geht ohne Rückfrage durch. Die Zeilenwerte werden bewusst NICHT umgerechnet —
    // das würde die vom Nutzer eingetragenen Zahlen verfälschen; der Wechsel ist eine
    // bestätigte Neuinterpretation.
    const menge = mengeById.get(position.id) ?? 0;
    const kalkMenge = menge > 0 ? menge : 1;
    const vorschau = berechneMengenBezugWechselVorschau(kalkulation, ziel, kalkMenge, defaults);
    if (vorschau.aendertPreis) {
      const einheitLabel = position.einheit?.trim() || 'Einh.';
      const zielLabel = ziel === 'einheit' ? `je ${einheitLabel}` : 'gesamt';
      const bestaetigt = window.confirm(
        `Modus auf „${zielLabel}" umstellen?\n\n` +
          'Die eingetragenen Werte bleiben unverändert und werden neu gedeutet:\n' +
          `Einheitspreis: ${formatEuro(vorschau.epVorher)} → ${formatEuro(vorschau.epNachher)}\n` +
          `Gesamtpreis: ${formatEuro(vorschau.gesamtVorher)} → ${formatEuro(vorschau.gesamtNachher)}`,
      );
      if (!bestaetigt) return;
    }

    const neueKalkulation: PositionsKalkulation = wechsleMengenBezug(kalkulation, ziel);
    const erfolgreich = updatePositionKalkulation(position.id, neueKalkulation);
    if (!erfolgreich) {
      toast({
        title: 'Modus nicht umgestellt',
        description: 'Die Kalkulation konnte nicht aktualisiert werden. Bitte erneut versuchen.',
        variant: 'destructive',
      });
    }
  };

  const renderMengenBezugToggle = (position: Position) => {
    const modus = effektiverMengenBezug(position);
    const einheitLabel = position.einheit?.trim() || 'Einh.';
    // Review-Auflage C: ohne gespeicherte Kalkulation gibt es nichts umzustellen — die Wahl
    // würde als flüchtiger Vormerk auseinanderlaufen (Reload, Import als erster Schreibweg).
    // Der Umschalter zeigt dann den Default als Vorschau und ist deaktiviert.
    const deaktiviert = !position.kalkulation;
    const deaktiviertTitle = `Wird mit der ersten Eingabe festgelegt (Standard: je ${einheitLabel}) und kann danach hier umgestellt werden`;
    return (
      <div className="flex flex-col gap-0.5 text-[10px] leading-tight">
        <button
          type="button"
          disabled={deaktiviert}
          onClick={() => setzeMengenBezug(position, 'einheit')}
          title={
            deaktiviert
              ? deaktiviertTitle
              : `Kalkulation gilt je ${einheitLabel} — wird für Summen mit der Positionsmenge multipliziert`
          }
          className={`rounded px-1 py-0.5 text-left ${
            modus === 'einheit'
              ? `bg-amber-100 font-semibold text-amber-800 ${deaktiviert ? 'opacity-60' : ''}`
              : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          je {einheitLabel}
        </button>
        <button
          type="button"
          disabled={deaktiviert}
          onClick={() => setzeMengenBezug(position, 'gesamt')}
          title={deaktiviert ? deaktiviertTitle : 'Kalkulation gilt für die gesamte Position (nicht je Einheit)'}
          className={`rounded px-1 py-0.5 text-left ${
            modus === 'gesamt'
              ? 'bg-gray-200 font-semibold text-gray-800'
              : `text-gray-400 ${deaktiviert ? 'opacity-60' : 'hover:text-gray-600'}`
          }`}
        >
          gesamt
        </button>
      </div>
    );
  };

  const renderBlitzInput = (position: Position, spalte: BlitzSpalte, placeholder: string) => {
    const positionId = position.id;
    const wert = blitzWert(positionId, spalte);
    // P1-1: Remount-Key inkl. Reject-Nonce — nach einer abgelehnten Übernahme (siehe applyBlitz)
    // zwingt ein geänderter `key` React, die Zelle NEU zu mounten, statt den nie gespeicherten,
    // im DOM stehen gebliebenen Wert weiter anzuzeigen.
    const zellenSchluessel = `${positionId}-${spalte}`;
    const rejectNonce = blitzRejectNonce[zellenSchluessel] ?? 0;
    // Vorfall Malermeister: die Eingabefelder beschriften jetzt den Bezug, statt nur eine nackte
    // Zahl entgegenzunehmen — z.B. "0,1" + Bildunterschrift "h je m" liest sich als "0,1 h je m".
    const modus = effektiverMengenBezug(position);
    const einheitLabel = position.einheit?.trim() || 'Einh.';
    const bezugCaption = modus === 'einheit' ? `je ${einheitLabel}` : 'gesamt';
    return (
      <div className="flex flex-col items-end">
        <BlitzZellInput
          key={`${zellenSchluessel}-${rejectNonce}`}
          positionId={positionId}
          spalte={spalte}
          value={wert}
          placeholder={placeholder}
          onCommit={(parsed) => applyBlitz(positionId, spalte, parsed)}
          onEnter={() => focusNext(positionId, spalte)}
        />
        <span className="mt-0.5 text-[10px] leading-none text-gray-400">{bezugCaption}</span>
      </div>
    );
  };

  return (
    <div ref={containerRef} className="flex h-full flex-col" data-testid="blitzkalkulation-view">
      {/* Kopfleiste: globale Defaults */}
      <div className="flex flex-wrap items-end gap-4 border-b bg-amber-50 px-4 py-3">
        <div className="flex items-center gap-2 text-amber-700">
          <Zap className="h-5 w-5" />
          <span className="font-semibold">Blitzkalkulation</span>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wide text-gray-500">Stundensatz €/h</label>
          <Input
            type="text"
            inputMode="decimal"
            key={`satz-${defaultsLoaded}`}
            defaultValue={String(defaults.stundensatz).replace('.', ',')}
            onChange={(e) => {
              const parsed = parseInput(e.target.value);
              if (parsed !== undefined) setKalkulationsDefaults({ stundensatz: parsed });
            }}
            className="h-8 w-28 text-right"
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wide text-gray-500">Material-Aufschlag %</label>
          <Input
            type="text"
            inputMode="decimal"
            key={`aufschlag-${defaultsLoaded}`}
            defaultValue={String(defaults.materialAufschlagProzent).replace('.', ',')}
            onChange={(e) => {
              const parsed = parseInput(e.target.value);
              if (parsed !== undefined) setKalkulationsDefaults({ materialAufschlagProzent: parsed });
            }}
            className="h-8 w-28 text-right"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={saveDefaults}
          disabled={isSavingDefaults}
          className="h-8"
        >
          {isSavingDefaults ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />}
          Als Standard speichern
        </Button>
        <CatalogBatchSuggestDialog />
        <div className="ml-auto text-right text-sm">
          {gesamt.netto > 0 ? (
            <>
              <p className="text-xs text-gray-500">
                Lohn {formatEuro(gesamt.lohn)} ({String(Math.round(gesamt.stunden * 100) / 100).replace('.', ',')} h)
                {' · '}Material {formatEuro(gesamt.material)}
                {' · '}Sonstiges {formatEuro(gesamt.sonstiges)}
              </p>
              <p className="font-semibold text-amber-800">Kalkulierte Summe: {formatEuro(gesamt.netto)}</p>
            </>
          ) : (
            <p className="text-gray-500">Kalkulierte Summe: noch keine Blitz-Eingaben</p>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setKalkulationsView('standard')}
          className="h-8 text-gray-600"
        >
          Zur Angebotstabelle
        </Button>
      </div>

      {/* Gesamtaufschlag / Rabatt: einmaliger Auf-/Abschlag über alle Positionen */}
      <div className="flex flex-wrap items-end gap-4 border-b bg-purple-50 px-4 py-3">
        <div className="flex items-center gap-2 text-purple-700">
          <Percent className="h-5 w-5" />
          <span className="font-semibold">Gesamtaufschlag / Rabatt</span>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wide text-gray-500">Prozent (±%)</label>
          <Input
            type="text"
            inputMode="decimal"
            value={gesamtaufschlagProzent}
            onChange={(e) => setGesamtaufschlagProzent(e.target.value)}
            placeholder="z.B. 8 oder -5"
            className="h-8 w-28 text-right"
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wide text-gray-500">Bezeichnung</label>
          <Input
            list="gesamtaufschlag-vorschlaege"
            value={gesamtaufschlagBezeichnung}
            onChange={(e) => setGesamtaufschlagBezeichnung(e.target.value)}
            placeholder="Wagnis & Gewinn"
            className="h-8 w-48"
          />
          <datalist id="gesamtaufschlag-vorschlaege">
            <option value="Wagnis & Gewinn" />
            <option value="Rabatt" />
          </datalist>
        </div>
        <div className="text-sm">
          {gesamtaufschlagErgebnisse ? (
            <p className="text-gray-700">
              Netto {formatEuro(gesamtaufschlagVorherSumme)}
              {' → '}
              <span className="font-semibold text-purple-800">
                {formatEuro(gesamtaufschlagNachherSumme ?? gesamtaufschlagVorherSumme)}
              </span>
            </p>
          ) : (
            <p className="text-gray-400">Prozent eingeben, um die Auswirkung zu sehen.</p>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={anwendenGesamtaufschlag}
          disabled={!gesamtaufschlagErgebnisse}
          className="h-8"
        >
          Auf alle Positionen anwenden
        </Button>
      </div>

      {/* Tabelle */}
      <div className="min-h-0 flex-1 overflow-auto">
        {rows.length === 0 ? (
          <p className="p-8 text-center text-sm text-gray-500">
            Keine Positionen geladen. Bitte zuerst ein LV importieren oder Positionen anlegen.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-gray-100 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 w-[90px]">Pos.</th>
                <th className="px-3 py-2">Bezeichnung</th>
                <th className="px-3 py-2 w-[110px] text-right">Menge</th>
                <th className="px-2 py-2 w-[100px]" title="Legt fest, ob Stunden/EK/Sonstiges dieser Position je Einheit oder für die gesamte Position gelten">
                  Modus
                </th>
                <th className="px-3 py-2 w-[100px] text-right">Stunden</th>
                <th className="px-3 py-2 w-[110px] text-right">Material-EK €</th>
                <th className="px-3 py-2 w-[110px] text-right">Sonstiges €</th>
                <th className="px-3 py-2 w-[100px] text-right">Lohn €</th>
                <th className="px-3 py-2 w-[110px] text-right">Material €</th>
                <th className="px-3 py-2 w-[100px] text-right">EP €</th>
                <th className="px-3 py-2 w-[110px] text-right">Gesamt €</th>
                <th className="px-2 py-2 w-[50px] text-center">Katalog</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((position, index) => {
                const menge = mengeById.get(position.id) ?? 0;
                const kalkMenge = menge > 0 ? menge : 1;
                const summen = berechneKalkulation(position.kalkulation, defaults);
                const kalkuliert = summen.gesamtNetto > 0;
                const ep = kalkuliert
                  ? einheitspreisAusKalkulation(position.kalkulation, kalkMenge, defaults)
                  : Number(position.preis) || 0;
                const zeilenGesamt = menge > 0 ? ep * menge : kalkuliert ? summen.gesamtNetto : 0;
                return (
                  <tr
                    key={position.id}
                    className={`border-b border-gray-100 ${kalkuliert ? 'bg-amber-50/50' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                  >
                    <td className="px-3 py-1.5 font-medium text-blue-600">{position.positionNumber || '-'}</td>
                    <td className="max-w-[320px] truncate px-3 py-1.5" title={position.name || position.beschreibung}>
                      {position.name || position.beschreibung || '-'}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {menge > 0 ? `${String(menge).replace('.', ',')} ${position.einheit || ''}` : '-'}
                    </td>
                    <td className="px-2 py-1.5">{renderMengenBezugToggle(position)}</td>
                    <td className="px-2 py-1">{renderBlitzInput(position, 'stunden', '0')}</td>
                    <td className="px-2 py-1">{renderBlitzInput(position, 'materialEk', '0,00')}</td>
                    <td className="px-2 py-1">{renderBlitzInput(position, 'sonstiges', '0,00')}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">
                      {summen.lohnSumme > 0 ? formatEuro(summen.lohnSumme) : '-'}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">
                      {summen.materialSumme > 0 ? formatEuro(summen.materialSumme) : '-'}
                    </td>
                    <td className={`px-3 py-1.5 text-right font-medium tabular-nums ${kalkuliert ? 'text-amber-800' : 'text-gray-700'}`}>
                      {formatEuro(ep)}
                    </td>
                    <td className="px-3 py-1.5 text-right font-medium tabular-nums text-primary">
                      {zeilenGesamt > 0 ? formatEuro(zeilenGesamt) : '-'}
                    </td>
                    <td className="px-2 py-1 text-center">
                      <CatalogSuggestDialog position={position} menge={kalkMenge} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
